import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MapPin, Mail, ChevronDown, Sparkles, Users, Rocket, TrendingUp } from 'lucide-react';
import { profile, experience } from '../../data/mock';
import { Badge } from '../ui/badge';
import InteractiveGrid from './InteractiveGrid';

const statCards = [
  { label: 'Years XP', value: profile.stats.yearsXP, icon: Sparkles, color: 'emerald' },
  { label: 'Projects Shipped', value: profile.stats.projectsShipped, icon: Rocket, color: 'cyan' },
  { label: 'Team Led', value: profile.stats.teamLed, icon: Users, color: 'amber' },
  { label: 'Efficiency Boost', value: profile.stats.systemEfficiency, icon: TrendingUp, color: 'rose' },
];

const colorMap = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', glow: 'shadow-emerald-500/5' },
  cyan: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/20', text: 'text-cyan-400', glow: 'shadow-cyan-500/5' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-400', glow: 'shadow-amber-500/5' },
  rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-400', glow: 'shadow-rose-500/5' },
};

export default function Hero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [firstName, ...restName] = profile.name.split(' ');
  const lastName = restName.join(' ');
  const currentRole = experience.find((item) => item.type === 'ACTIVE_QUEST');

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Interactive background grid */}
      <InteractiveGrid />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-500/[0.03] rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-cyan-500/[0.03] rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
        <div className="grid lg:grid-cols-[1fr_380px] gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div>
            {/* Status badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5 px-3 py-1.5 font-mono text-xs mb-6 inline-flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Available for Quests
              </Badge>
            </motion.div>

            {/* Name */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-4"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              <span className="text-zinc-100">{firstName}</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-emerald-400">{lastName}</span>
            </motion.h1>

            {/* Title with level */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="font-mono text-lg text-zinc-400">{profile.title}</span>
              <span className="font-mono text-xs px-2 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">
                LVL {profile.level}
              </span>
            </motion.div>

            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-zinc-400 text-lg leading-relaxed max-w-xl mb-4"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              {profile.tagline}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.34 }}
              className="text-zinc-500 text-base leading-relaxed max-w-2xl mb-6"
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              {profile.summary}
            </motion.p>

            {/* Industries */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.35 }}
              className="flex flex-wrap gap-2 mb-8"
            >
              {profile.industries.map((industry) => (
                <span key={industry} className="px-3 py-1 rounded-full bg-cyan-500/8 border border-cyan-500/15 font-mono text-xs text-cyan-400/80">
                  {industry}
                </span>
              ))}
            </motion.div>

            {/* Contact chips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="flex flex-wrap gap-3 mb-10"
            >
              <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f0f18] border border-[#1a1a2e] hover:border-emerald-500/30 text-zinc-400 hover:text-emerald-400 font-mono text-sm transition-all group">
                <Mail size={15} className="text-zinc-600 group-hover:text-emerald-500 transition-colors" />
                {profile.email}
              </a>
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0f0f18] border border-[#1a1a2e] text-zinc-400 font-mono text-sm">
                <MapPin size={15} className="text-zinc-600" />
                {profile.location}
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="flex flex-wrap gap-4"
            >
              <a
                href="#contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#050508] font-semibold text-sm transition-colors"
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                <Sparkles size={16} />
                Start a Quest
              </a>
              <a
                href="#quests"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-[#1a1a2e] hover:border-zinc-600 text-zinc-300 hover:text-zinc-100 font-semibold text-sm transition-all"
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                View Quest Log
              </a>
            </motion.div>
          </div>

          {/* Right: Player card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Animated glow border */}
              <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-b from-emerald-500/20 via-transparent to-cyan-500/20 animate-pulse" style={{ animationDuration: '3s' }} />
              <div className="absolute -inset-4 bg-emerald-500/[0.04] rounded-3xl blur-2xl" />
              
              <div className="relative rounded-2xl border border-[#1a1a2e] bg-[#0a0a14]/90 backdrop-blur-sm overflow-hidden">
                {/* Card top bar */}
                <div className="px-5 py-3 border-b border-[#1a1a2e] flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500/60" />
                  <span className="ml-auto font-mono text-[10px] text-zinc-700">player_card.tsx</span>
                </div>

                {/* Avatar + Identity */}
                <div className="px-6 pt-5 pb-4">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative">
                      <div className="w-[72px] h-[72px] rounded-xl bg-gradient-to-br from-emerald-500/25 to-cyan-500/25 border border-emerald-500/25 flex items-center justify-center">
                        <span className="font-bold text-2xl text-emerald-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          {profile.avatarInitials}
                        </span>
                      </div>
                      {/* Online indicator */}
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#0a0a14] flex items-center justify-center">
                        <span className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                      </div>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-bold text-zinc-100 text-lg leading-tight" style={{ fontFamily: 'Figtree, sans-serif' }}>{profile.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-xs px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                          LVL {profile.level}
                        </span>
                        <span className="font-mono text-[10px] text-zinc-600">|</span>
                        <span className="font-mono text-xs text-amber-400/80">{profile.class}</span>
                      </div>
                      <p className="font-mono text-[10px] text-zinc-500 mt-1 italic">{profile.classSubtitle}</p>
                    </div>
                  </div>
                </div>

                {/* XP Bar */}
                <div className="px-6 pb-4">
                  <div className="p-3 rounded-lg bg-[#080810] border border-[#141420]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Career XP</span>
                      <span className="font-mono text-xs text-amber-400 font-semibold">{profile.xp.toLocaleString()} / {profile.maxXp.toLocaleString()}</span>
                    </div>
                    <div className="w-full h-2.5 rounded-full bg-[#1a1a2e] overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${(profile.xp / profile.maxXp) * 100}%` } : {}}
                        transition={{ duration: 1.2, delay: 0.8, ease: 'easeOut' }}
                        className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-400 relative"
                      >
                        <div className="absolute right-0 top-0 bottom-0 w-3 bg-white/25 rounded-full blur-sm" />
                      </motion.div>
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="font-mono text-[9px] text-zinc-700">LVL {profile.level}</span>
                      <span className="font-mono text-[9px] text-zinc-700">LVL {profile.level + 1}</span>
                    </div>
                  </div>
                </div>

                {/* Quick stats grid */}
                <div className="px-6 pb-5">
                  <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Quick Stats</span>
                  <div className="grid grid-cols-2 gap-2.5 mt-2.5">
                    {statCards.map((stat, i) => {
                      const c = colorMap[stat.color];
                      const Icon = stat.icon;
                      return (
                        <motion.div
                          key={stat.label}
                          initial={{ opacity: 0, y: 10 }}
                          animate={inView ? { opacity: 1, y: 0 } : {}}
                          transition={{ delay: 0.9 + i * 0.1 }}
                          className={`rounded-lg ${c.bg} border ${c.border} p-3 shadow-lg ${c.glow} group hover:scale-[1.03] transition-transform`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <Icon size={13} className={`${c.text}`} />
                            <p className="font-mono text-[10px] text-zinc-600">{stat.label}</p>
                          </div>
                          <p className={`font-bold text-xl ${c.text}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                            {stat.value}
                          </p>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>

                {/* Current status bar */}
                <div className="px-6 py-3 border-t border-[#1a1a2e] bg-[#080810]/50">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-mono text-[10px] text-zinc-500">
                      Currently shipping as <span className="text-emerald-400/80">{currentRole?.title}</span>{' '}
                      at <span className="text-emerald-400/80">{currentRole?.company}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            <ChevronDown size={20} className="text-zinc-600" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
